import AccountTreeOutlinedIcon from '@mui/icons-material/AccountTreeOutlined';
import AccountBalanceOutlinedIcon from '@mui/icons-material/AccountBalanceOutlined';
import ReceiptLongOutlinedIcon from '@mui/icons-material/ReceiptLongOutlined';
import MenuBookOutlinedIcon from '@mui/icons-material/MenuBookOutlined';
import BalanceOutlinedIcon from '@mui/icons-material/BalanceOutlined';
import RequestQuoteOutlinedIcon from '@mui/icons-material/RequestQuoteOutlined';
import PaymentsOutlinedIcon from '@mui/icons-material/PaymentsOutlined';
import AccountBalanceWalletOutlinedIcon from '@mui/icons-material/AccountBalanceWalletOutlined';
import SavingsOutlinedIcon from '@mui/icons-material/SavingsOutlined';
import DevicesOtherOutlinedIcon from '@mui/icons-material/DevicesOtherOutlined';
import SwapHorizOutlinedIcon from '@mui/icons-material/SwapHorizOutlined';
import TrendingDownOutlinedIcon from '@mui/icons-material/TrendingDownOutlined';
import FactCheckOutlinedIcon from '@mui/icons-material/FactCheckOutlined';
import SummarizeOutlinedIcon from '@mui/icons-material/SummarizeOutlined';
import VerifiedUserOutlinedIcon from '@mui/icons-material/VerifiedUserOutlined';
import ChecklistOutlinedIcon from '@mui/icons-material/ChecklistOutlined';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import PeopleOutlinedIcon from '@mui/icons-material/PeopleOutlined';
import StorefrontOutlinedIcon from '@mui/icons-material/StorefrontOutlined';
import SettingsOutlinedIcon from '@mui/icons-material/SettingsOutlined';
import CategoryOutlinedIcon from '@mui/icons-material/CategoryOutlined';
import CalendarMonthOutlinedIcon from '@mui/icons-material/CalendarMonthOutlined';
import PlaylistAddCheckOutlinedIcon from '@mui/icons-material/PlaylistAddCheckOutlined';
import ListAltOutlinedIcon from '@mui/icons-material/ListAltOutlined';
import ShoppingCartOutlinedIcon from '@mui/icons-material/ShoppingCartOutlined';
import PointOfSaleOutlinedIcon from '@mui/icons-material/PointOfSaleOutlined';
import PercentOutlinedIcon from '@mui/icons-material/PercentOutlined';
import AssignmentOutlinedIcon from '@mui/icons-material/AssignmentOutlined';
import LocalShippingOutlinedIcon from '@mui/icons-material/LocalShippingOutlined';
import RuleOutlinedIcon from '@mui/icons-material/RuleOutlined';
import { stockNavGroup } from '@/modules/stock/nav';
import type { NavEntry, NavGroup, NavItem } from '@/navigation/navItems';
import { Perm } from '@/auth/permissions';
import { Modules } from '@/core/modules/moduleKeys';

const item = (label: string, path: string, icon: NavItem['icon'], comingSoon?: boolean): NavItem =>
  ({ label, path, icon, module: Modules.Accounting, permission: Perm.Accounting.View, ...(comingSoon ? { comingSoon } : {}) });

/**
 * The accounting module's navigation: one collapsible Accounting menu, under it the overview and the
 * ledgers a finance office keeps as collapsible sub-menus whose items are each ledger's sections; a
 * section's leaves live as tabs on its page. Shown only where the organisation has the module on.
 */
export const accountingNavEntries: NavEntry[] = [
  item('Overview', '/accounting', AccountBalanceOutlinedIcon),
  {
    kind: 'group', key: 'accounting-gl', label: 'General Ledger', icon: MenuBookOutlinedIcon,
    items: [
      item('Chart of Accounts', '/accounting/general-ledger/chart-of-accounts', ListAltOutlinedIcon),
      item('Transactions', '/accounting/general-ledger/transactions', ReceiptLongOutlinedIcon),
      item('Cash book', '/accounting/general-ledger/cash-book', AccountBalanceWalletOutlinedIcon),
      item('Budgets', '/accounting/general-ledger/budgets', SavingsOutlinedIcon),
      item('Reports', '/accounting/general-ledger/reports', BalanceOutlinedIcon),
      item('Close', '/accounting/general-ledger/close', LockOutlinedIcon),
    ],
  },
  {
    kind: 'group', key: 'accounting-sales', label: 'Sales Ledger', icon: PointOfSaleOutlinedIcon,
    items: [
      item('Customers', '/accounting/sales-ledger/customers', PeopleOutlinedIcon),
      item('Quotations', '/accounting/sales-ledger/transactions/quotation', AssignmentOutlinedIcon),
      item('Transactions', '/accounting/sales-ledger/transactions', RequestQuoteOutlinedIcon),
      item('Deliveries', '/accounting/sales-ledger/transactions/delivery', LocalShippingOutlinedIcon),
      item('Reports', '/accounting/sales-ledger/reports', SummarizeOutlinedIcon),
      item('Credit Control', '/accounting/sales-ledger/credit-control', VerifiedUserOutlinedIcon),
      item('Close', '/accounting/sales-ledger/close', LockOutlinedIcon),
    ],
  },
  {
    kind: 'group', key: 'accounting-purchase', label: 'Purchase Ledger', icon: ShoppingCartOutlinedIcon,
    items: [
      item('Suppliers', '/accounting/purchase-ledger/suppliers', StorefrontOutlinedIcon),
      item('Requisitions', '/accounting/purchase-ledger/requisitions', AssignmentOutlinedIcon),
      item('Purchase Orders', '/accounting/purchase-ledger/purchase-orders', LocalShippingOutlinedIcon),
      item('Transactions', '/accounting/purchase-ledger/transactions', PaymentsOutlinedIcon),
      item('Match Exceptions', '/accounting/purchase-ledger/match-exceptions', RuleOutlinedIcon),
      item('Reports', '/accounting/purchase-ledger/reports', SummarizeOutlinedIcon),
      item('Payment Runs', '/accounting/purchase-ledger/payment-runs', PaymentsOutlinedIcon),
      item('Close', '/accounting/purchase-ledger/close', LockOutlinedIcon),
    ],
  },
  // Stock is a balance-sheet asset the finance team values and reconciles, so it sits with the ledgers,
  // immediately before the assets register. It disappears with its module, as every entry here does.
  stockNavGroup,
  {
    kind: 'group', key: 'accounting-fixed-assets', label: 'Fixed Assets', icon: DevicesOtherOutlinedIcon,
    items: [
      item('Register', '/accounting/fixed-assets/register', DevicesOtherOutlinedIcon),
      item('Asset Events', '/accounting/fixed-assets/events', SwapHorizOutlinedIcon),
      item('Depreciation', '/accounting/fixed-assets/depreciation', TrendingDownOutlinedIcon),
      item('Physical Verification', '/accounting/fixed-assets/verification', FactCheckOutlinedIcon),
      item('Capital Projects', '/accounting/fixed-assets/capital-projects', AccountTreeOutlinedIcon),
    ],
  },
  {
    kind: 'group', key: 'accounting-settings', label: 'Accounting Settings', icon: SettingsOutlinedIcon,
    items: [
      item('Settings', '/accounting/settings', SettingsOutlinedIcon),
      item('Cost Centres', '/accounting/settings/cost-centres', CategoryOutlinedIcon),
      item('Fiscal Calendar', '/accounting/settings/fiscal-calendar', CalendarMonthOutlinedIcon),
      item('Tax Codes', '/accounting/settings/tax-codes', PercentOutlinedIcon),
      item('Opening Balances', '/accounting/settings/opening-balances', PlaylistAddCheckOutlinedIcon),
      item('Close Checklist', '/accounting/settings/close-checklist', ChecklistOutlinedIcon),
      item('Controls', '/accounting/settings/controls', VerifiedUserOutlinedIcon),
    ],
  },
];

export const accountingNavGroup: NavGroup = {
  kind: 'group', key: 'accounting', label: 'Accounting', icon: AccountBalanceOutlinedIcon, items: accountingNavEntries,
};

/**
 * Where each of the module's former flat tabs now lives. The redirect routes and the overview's
 * checklist both resolve through this map, so a stored notification link, a bookmark or an old
 * `?tab=` query still lands on the right page.
 */
export const legacyAccountingPaths: Record<string, string> = {
  overview: '/accounting',
  journals: '/accounting/general-ledger/transactions/journal',
  recurring: '/accounting/general-ledger/transactions/recurring',
  ledger: '/accounting/general-ledger/reports/account-ledger',
  'trial-balance': '/accounting/general-ledger/reports/trial-balance',
  statements: '/accounting/general-ledger/reports/profit-and-loss',
  bank: '/accounting/general-ledger/cash-book/accounts',
  payments: '/accounting/general-ledger/cash-book/transactions',
  accounts: '/accounting/general-ledger/chart-of-accounts',
  close: '/accounting/general-ledger/close',
  invoices: '/accounting/sales-ledger/transactions/invoice',
  customers: '/accounting/sales-ledger/customers',
  bills: '/accounting/purchase-ledger/transactions/invoice',
  suppliers: '/accounting/purchase-ledger/suppliers',
  budgets: '/accounting/general-ledger/budgets',
  settings: '/accounting/settings',
  'cost-centres': '/accounting/settings/cost-centres',
  calendar: '/accounting/settings/fiscal-calendar',
  'opening-balances': '/accounting/settings/opening-balances',
  controls: '/accounting/settings/controls',
};
